import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Eraser, ExternalLink, ImagePlus, RefreshCw, Send, Trash2 } from "lucide-react";
import { formatAccountNo } from "@/lib/accounts";
import {
  type Cleanup,
  LIMITS,
  type OwnChat,
  PERMISSION_KEYS,
  type Permissions,
  type Posted,
  SLOW_MODE_CHOICES,
  chatUrl,
  errorText,
  useFirstRead,
  usePostToOwnChat,
  useRefreshOwnChat,
  useRemoveServiceLines,
  useSetDiscussion,
  useSetOwnChatPhoto,
  useSetOwnChatRules,
  useSetOwnChatUsername,
  useUpdateOwnChat,
} from "@/lib/ownChats";
import { formatNumber, formatRelative } from "@/lib/format";
import { pushToast } from "@/components/Toaster";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DetailDrawer } from "@/components/ui/drawer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SimpleSelect } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { type TabItem } from "@/components/ui/tabs";
import { cn } from "@/lib/cn";
import { Choice, TEXTAREA, UsernameField } from "./CreateOwnChatDialog";
import { CopyButton, Links } from "./OwnChatLinks";
import { Admins, Members } from "./OwnChatPeople";
import { SecondAdminCard } from "./SecondAdmin";

/**
 * A chat's card: what Telegram says about it, and everything that can be
 * changed from here — its name and whether it is open, admins, members, the
 * ways in, a group's rules — plus posting into it and clearing its service
 * lines. Each change is made in Telegram while the button spins.
 */
export function OwnChatDrawer({
  chat,
  chats,
  open,
  onOpenChange,
  onDelete,
}: {
  chat: OwnChat | null;
  /** The whole list — a channel picks its discussion group from it. */
  chats: OwnChat[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDelete: (chat: OwnChat, inTelegram: boolean) => void;
}) {
  const { t } = useTranslation();
  // The page keys this card by its chat, so another chat starts on the first
  // tab; closing puts it back for the same chat. Never an effect: for one
  // render the old tab would be showing, and its list would be read from
  // Telegram for nothing.
  const [tab, setTab] = useState("main");
  // A post being written lives here, not in its tab: a tab's own state is gone
  // the moment another tab is opened — to copy the chat's address, say.
  const [draft, setDraft] = useState<PostDraft>(NO_DRAFT);

  if (!chat) return null;
  const url = chatUrl(chat);
  const count = (n: number) => <span className="font-mono text-xs text-fg-muted">{n}</span>;

  const tabs: TabItem[] = [
    {
      value: "main",
      label: t("own_chats.tab.main"),
      content: <Main key={chat.id} chat={chat} chats={chats} />,
    },
    {
      value: "admins",
      label: t("own_chats.tab.admins"),
      content: <Admins key={chat.id} chat={chat} active={tab === "admins"} />,
    },
    {
      value: "members",
      label: (
        <>
          {t(`own_chats.tab.members_${chat.kind}`)}
          {chat.members_count !== null && count(chat.members_count)}
        </>
      ),
      content: <Members key={chat.id} chat={chat} active={tab === "members"} />,
    },
    {
      value: "links",
      label: (
        <>
          {t("own_chats.tab.links")}
          {chat.requests_pending > 0 && count(chat.requests_pending)}
        </>
      ),
      content: <Links key={chat.id} chat={chat} active={tab === "links"} />,
    },
    ...(chat.kind === "group"
      ? [
          {
            value: "rules",
            label: t("own_chats.tab.rules"),
            content: <Rules key={chat.id} chat={chat} />,
          },
        ]
      : []),
    {
      value: "post",
      label: t("own_chats.tab.post"),
      content: <Post key={chat.id} chat={chat} draft={draft} onDraft={setDraft} />,
    },
  ];

  return (
    <DetailDrawer
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) setTab("main");
      }}
      title={chat.title}
      breadcrumbs={[{ label: t("own_chats.title") }, { label: t(`own_chats.kind.${chat.kind}`) }]}
      status={{
        domain: "own_chat",
        value: chat.username ? "open" : "closed",
        label: t(chat.username ? "own_chats.access.open" : "own_chats.access.closed"),
      }}
      tabs={tabs}
      activeTab={tab}
      onTabChange={setTab}
      footer={
        <>
          <Button
            variant="ghost"
            className="mr-auto"
            onClick={() => onDelete(chat, false)}
            title={t("own_chats.forget_hint")}
          >
            {t("own_chats.forget")}
          </Button>
          {/* On a phone the two on the right keep their icons and lose the words. */}
          {chat.is_creator && (
            <Button
              variant="ghost"
              className="shrink-0 text-danger hover:text-danger"
              onClick={() => onDelete(chat, true)}
              aria-label={t("own_chats.delete")}
              title={t("own_chats.delete")}
              iconLeft={<Trash2 size={15} strokeWidth={2} aria-hidden="true" />}
            >
              <span className="hidden sm:inline">{t("own_chats.delete")}</span>
            </Button>
          )}
          {url && (
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              aria-label={t("own_chats.open_in_telegram")}
              title={t("own_chats.open_in_telegram")}
              className="inline-flex h-10 shrink-0 items-center gap-2 rounded-lg border border-border px-3 text-sm font-medium text-fg-primary transition-colors hover:bg-bg-elevated/50 sm:px-4"
            >
              <ExternalLink size={15} strokeWidth={2} aria-hidden="true" />
              <span className="hidden sm:inline">{t("own_chats.open_in_telegram")}</span>
            </a>
          )}
        </>
      }
    />
  );
}
OwnChatDrawer.displayName = "OwnChatDrawer";

interface PostDraft {
  text: string;
  file: File | null;
  pin: boolean;
}

const NO_DRAFT: PostDraft = { text: "", file: null, pin: false };

function Section({ title, children }: { title: ReactNode; children: ReactNode }) {
  return (
    <section className="space-y-3 rounded-xl border border-border bg-bg-primary p-4">
      <h3 className="text-sm font-semibold text-fg-primary">{title}</h3>
      {children}
    </section>
  );
}

function Fact({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 py-2">
      <span className="shrink-0 text-sm text-fg-muted">{label}</span>
      <span className="ml-auto flex min-w-0 items-center gap-1.5 text-right text-sm text-fg-primary">
        {children}
      </span>
    </div>
  );
}

/* -------------------------------------------------------------------- main */

function Main({ chat, chats }: { chat: OwnChat; chats: OwnChat[] }) {
  const { t } = useTranslation();
  const first = useFirstRead(chat);
  const refresh = useRefreshOwnChat();
  const reading = first.isFetching || refresh.isPending;

  return (
    <div className="space-y-4">
      {!chat.is_creator && <Alert tone="info">{t("own_chats.main.admin_only")}</Alert>}
      {first.isError && !reading && chat.synced_at === null && (
        <Alert tone="danger">{errorText(first.error, t("own_chats.load_error"))}</Alert>
      )}

      <div className="divide-y divide-border rounded-xl border border-border bg-bg-primary px-4">
        <Fact label={t("own_chats.main.account")}>
          <span className="truncate">
            {formatAccountNo(chat.account_seq_no)} · {chat.account_label}
          </span>
          <span className="shrink-0 text-fg-muted">
            {t(chat.is_creator ? "own_chats.role.owner" : "own_chats.role.admin")}
          </span>
        </Fact>
        <Fact label={t(`own_chats.main.members_${chat.kind}`)}>
          {chat.members_count !== null ? formatNumber(chat.members_count) : "—"}
        </Fact>
        {chat.username && (
          <Fact label={t("own_chats.main.address")}>
            <span className="truncate font-mono">t.me/{chat.username}</span>
            <CopyButton text={`https://t.me/${chat.username}`} />
          </Fact>
        )}
        {chat.invite_link && (
          <Fact label={t("own_chats.main.invite_link")}>
            <span className="truncate font-mono">{chat.invite_link.replace("https://", "")}</span>
            <CopyButton text={chat.invite_link} />
          </Fact>
        )}
        <Fact label={t("own_chats.main.synced")}>
          <span className="text-fg-muted">
            {reading
              ? t("own_chats.main.syncing")
              : chat.synced_at
                ? formatRelative(chat.synced_at)
                : t("own_chats.main.never_synced")}
          </span>
          <Button
            size="sm"
            variant="ghost"
            disabled={reading}
            onClick={() => refresh.mutate({ id: chat.id })}
            iconLeft={<RefreshCw size={14} strokeWidth={2} aria-hidden="true" />}
          >
            {t("own_chats.reread")}
          </Button>
        </Fact>
      </div>

      <NameAndAbout chat={chat} locked={first.isFetching} />
      <Photo chat={chat} />
      <Access chat={chat} />
      {chat.kind === "channel" && <Discussion chat={chat} chats={chats} />}
      <ServiceLines chat={chat} />
      {chat.kind === "channel" && chat.is_creator && <SecondAdminCard chat={chat} />}
    </div>
  );
}

function NameAndAbout({ chat, locked }: { chat: OwnChat; locked: boolean }) {
  const { t } = useTranslation();
  const save = useUpdateOwnChat();
  const [title, setTitle] = useState(chat.title);
  const [about, setAbout] = useState(chat.about ?? "");
  // What Telegram says may arrive after the card opened (the first read).
  useEffect(() => {
    setTitle(chat.title);
    setAbout(chat.about ?? "");
  }, [chat.title, chat.about]);
  const dirty = title.trim() !== chat.title || about.trim() !== (chat.about ?? "");

  return (
    <Section title={t("own_chats.main.name_title")}>
      <div className="space-y-2">
        <Label htmlFor="oc_edit_title">{t("own_chats.field.title")}</Label>
        <Input
          id="oc_edit_title"
          value={title}
          disabled={locked}
          maxLength={LIMITS.title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="oc_edit_about">{t("own_chats.field.about")}</Label>
        <textarea
          id="oc_edit_about"
          value={about}
          disabled={locked}
          maxLength={LIMITS.about}
          rows={3}
          onChange={(e) => setAbout(e.target.value)}
          className={cn(TEXTAREA, "resize-y")}
        />
      </div>
      {dirty && (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            disabled={!title.trim() || save.isPending}
            loading={save.isPending}
            onClick={() =>
              save.mutate(
                { id: chat.id, title: title.trim(), about: about.trim() },
                { onSuccess: () => pushToast({ kind: "success", text: t("own_chats.saved") }) },
              )
            }
          >
            {t("own_chats.save")}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setTitle(chat.title);
              setAbout(chat.about ?? "");
            }}
          >
            {t("common.cancel")}
          </Button>
        </div>
      )}
    </Section>
  );
}

function Photo({ chat }: { chat: OwnChat }) {
  const { t } = useTranslation();
  const save = useSetOwnChatPhoto();
  const input = useRef<HTMLInputElement>(null);
  const done = (key: string) => () => pushToast({ kind: "success", text: t(key) });

  return (
    <Section title={t("own_chats.main.photo_title")}>
      <p className="text-xs leading-relaxed text-fg-muted">{t("own_chats.main.photo_hint")}</p>
      <input
        ref={input}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) {
            save.mutate({ id: chat.id, file }, { onSuccess: done("own_chats.main.photo_set") });
          }
        }}
      />
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="outline"
          loading={save.isPending && save.variables?.file !== null}
          disabled={save.isPending}
          onClick={() => input.current?.click()}
          iconLeft={<ImagePlus size={15} strokeWidth={2} aria-hidden="true" />}
        >
          {t("own_chats.main.photo_upload")}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          loading={save.isPending && save.variables?.file === null}
          disabled={save.isPending}
          onClick={() =>
            save.mutate(
              { id: chat.id, file: null },
              { onSuccess: done("own_chats.main.photo_removed") },
            )
          }
        >
          {t("own_chats.main.photo_remove")}
        </Button>
      </div>
    </Section>
  );
}

function Access({ chat }: { chat: OwnChat }) {
  const { t } = useTranslation();
  const save = useSetOwnChatUsername();
  const [isOpen, setIsOpen] = useState(chat.username !== null);
  const [username, setUsername] = useState(chat.username ?? "");
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setIsOpen(chat.username !== null);
    setUsername(chat.username ?? "");
  }, [chat.username]);

  const name = username.trim().replace(/^@/, "");
  const wanted = isOpen ? name : null;
  const dirty = wanted !== chat.username && (!isOpen || name !== "");

  if (!chat.is_creator) {
    return (
      <Section title={t("own_chats.main.access_title")}>
        <p className="text-sm text-fg-muted">{t("own_chats.main.access_owner_only")}</p>
      </Section>
    );
  }

  const submit = async () => {
    setError(null);
    try {
      await save.mutateAsync({ id: chat.id, username: wanted });
      pushToast({
        kind: "success",
        text: t(wanted ? "own_chats.main.opened" : "own_chats.main.closed"),
      });
    } catch (err) {
      setError(errorText(err, t("own_chats.action_error")));
    }
  };

  return (
    <Section title={t("own_chats.main.access_title")}>
      <div
        role="radiogroup"
        aria-label={t("own_chats.main.access_title")}
        className="flex flex-col gap-2 sm:flex-row"
      >
        <Choice
          active={!isOpen}
          onClick={() => setIsOpen(false)}
          title={t("own_chats.access.closed")}
          hint={t("own_chats.access.closed_hint")}
        />
        <Choice
          active={isOpen}
          onClick={() => setIsOpen(true)}
          title={t("own_chats.access.open")}
          hint={t("own_chats.access.open_hint")}
        />
      </div>
      {isOpen && (
        <UsernameField
          id="oc_edit_username"
          value={username}
          onChange={setUsername}
          disabled={save.isPending}
        />
      )}
      {error && <Alert tone="danger">{error}</Alert>}
      {dirty && (
        <Button size="sm" loading={save.isPending} onClick={submit}>
          {t(wanted ? "own_chats.main.open_submit" : "own_chats.main.close_submit")}
        </Button>
      )}
    </Section>
  );
}

function Discussion({ chat, chats }: { chat: OwnChat; chats: OwnChat[] }) {
  const { t } = useTranslation();
  const save = useSetDiscussion();
  const [groupId, setGroupId] = useState("");
  // Only a group this same account owns can be attached.
  const groups = useMemo(
    () =>
      chats.filter(
        (c) => c.kind === "group" && c.account_id === chat.account_id && c.is_creator,
      ),
    [chats, chat.account_id],
  );
  const done = (key: string) => () => pushToast({ kind: "success", text: t(key) });

  return (
    <Section title={t("own_chats.discussion.title")}>
      <p className="text-xs leading-relaxed text-fg-muted">{t("own_chats.discussion.hint")}</p>
      {chat.linked ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="min-w-0 flex-1 truncate text-sm text-fg-primary">
            {chat.linked.title ?? t("own_chats.discussion.unlisted")}
          </span>
          {chat.is_creator && (
            <Button
              size="sm"
              variant="outline"
              loading={save.isPending}
              onClick={() =>
                save.mutate(
                  { id: chat.id, group_id: null },
                  { onSuccess: done("own_chats.discussion.detached") },
                )
              }
            >
              {t("own_chats.discussion.detach")}
            </Button>
          )}
        </div>
      ) : !chat.is_creator ? (
        <p className="text-sm text-fg-muted">{t("own_chats.discussion.owner_only")}</p>
      ) : groups.length === 0 ? (
        <p className="text-sm text-fg-muted">{t("own_chats.discussion.no_groups")}</p>
      ) : (
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-[12rem] flex-1">
            <SimpleSelect
              value={groupId}
              onValueChange={setGroupId}
              placeholder={t("own_chats.discussion.choose") ?? undefined}
              options={groups.map((g) => ({ value: g.id, label: g.title }))}
            />
          </div>
          <Button
            size="sm"
            disabled={!groupId || save.isPending}
            loading={save.isPending}
            onClick={() =>
              save.mutate(
                { id: chat.id, group_id: groupId },
                { onSuccess: done("own_chats.discussion.attached") },
              )
            }
          >
            {t("own_chats.discussion.attach")}
          </Button>
        </div>
      )}
    </Section>
  );
}

function ServiceLines({ chat }: { chat: OwnChat }) {
  const { t } = useTranslation();
  const clear = useRemoveServiceLines();
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<Cleanup | null>(null);

  const submit = async () => {
    setError(null);
    setDone(null);
    try {
      setDone(await clear.mutateAsync({ id: chat.id }));
    } catch (err) {
      setError(errorText(err, t("own_chats.action_error")));
    }
  };

  const summary = (d: Cleanup) =>
    [
      d.removed > 0
        ? t("own_chats.cleanup.removed", { count: d.removed })
        : t("own_chats.cleanup.nothing"),
      d.refused > 0 && t("own_chats.cleanup.refused", { count: d.refused }),
      d.creation_kept && t(`own_chats.cleanup.creation_kept_${chat.kind}`),
      !d.whole && t("own_chats.cleanup.not_whole", { looked: formatNumber(d.looked) }),
      d.warning,
    ]
      .filter(Boolean)
      .join(" ");

  return (
    <Section title={t("own_chats.cleanup.title")}>
      <p className="text-xs leading-relaxed text-fg-muted">
        {t(`own_chats.cleanup.hint_${chat.kind}`)}
      </p>
      {error && <Alert tone="danger">{error}</Alert>}
      {done && (
        <Alert tone={done.warning || done.refused > 0 ? "warning" : "success"}>
          {summary(done)}
        </Alert>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          loading={clear.isPending}
          onClick={submit}
          iconLeft={<Eraser size={15} strokeWidth={2} aria-hidden="true" />}
        >
          {t("own_chats.cleanup.submit")}
        </Button>
        {clear.isPending && (
          <span className="text-xs text-fg-muted">{t("own_chats.cleanup.working")}</span>
        )}
      </div>
    </Section>
  );
}

/* ------------------------------------------------------------------- rules */

const EVERYTHING = Object.fromEntries(PERMISSION_KEYS.map((k) => [k, true])) as Permissions;

function Rules({ chat }: { chat: OwnChat }) {
  const { t } = useTranslation();
  const save = useSetOwnChatRules();
  // What Telegram says may still be on its way; it would overwrite the switches.
  const locked = useFirstRead(chat).isFetching || save.isPending;
  const saved = chat.permissions ?? EVERYTHING;
  const [draft, setDraft] = useState<Permissions>(saved);
  const [slow, setSlow] = useState(chat.slow_mode_seconds);
  const savedKey = JSON.stringify(saved);
  useEffect(() => {
    setDraft(JSON.parse(savedKey) as Permissions);
    setSlow(chat.slow_mode_seconds);
  }, [savedKey, chat.slow_mode_seconds]);

  const dirty =
    slow !== chat.slow_mode_seconds || PERMISSION_KEYS.some((k) => draft[k] !== saved[k]);

  return (
    <div className="space-y-4">
      <Section title={t("own_chats.rules.members_title")}>
        <ul className="space-y-3">
          {PERMISSION_KEYS.map((key) => (
            <li key={key}>
              <Switch
                checked={draft[key]}
                // With no writing at all the rest about writing means nothing.
                disabled={
                  locked ||
                  (!draft.send_messages &&
                    (key === "send_media" || key === "send_polls" || key === "embed_links"))
                }
                onCheckedChange={(on) => setDraft((d) => ({ ...d, [key]: on }))}
                label={t(`own_chats.permission.${key}`)}
              />
            </li>
          ))}
        </ul>
      </Section>

      <Section title={t("own_chats.rules.slow_title")}>
        <p className="text-xs leading-relaxed text-fg-muted">{t("own_chats.rules.slow_hint")}</p>
        <SimpleSelect
          value={String(slow)}
          onValueChange={(v) => setSlow(Number(v))}
          disabled={locked}
          className="max-w-[16rem]"
          options={SLOW_MODE_CHOICES.map((seconds) => ({
            value: String(seconds),
            label: t(`own_chats.rules.slow_${seconds}`),
          }))}
        />
      </Section>

      {dirty && (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            disabled={save.isPending}
            loading={save.isPending}
            onClick={() =>
              save.mutate(
                { id: chat.id, permissions: draft, slow_mode_seconds: slow },
                { onSuccess: () => pushToast({ kind: "success", text: t("own_chats.saved") }) },
              )
            }
          >
            {t("own_chats.save")}
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              setDraft(saved);
              setSlow(chat.slow_mode_seconds);
            }}
          >
            {t("common.cancel")}
          </Button>
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------- post */

function Post({
  chat,
  draft,
  onDraft,
}: {
  chat: OwnChat;
  draft: PostDraft;
  onDraft: (draft: PostDraft) => void;
}) {
  const { t } = useTranslation();
  const post = usePostToOwnChat();
  const { text, file, pin } = draft;
  const setText = (value: string) => onDraft({ ...draft, text: value });
  const setFile = (value: File | null) => onDraft({ ...draft, file: value });
  const setPin = (value: boolean) => onDraft({ ...draft, pin: value });
  const [error, setError] = useState<string | null>(null);
  const [posted, setPosted] = useState<Posted | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const limit = file ? LIMITS.caption : LIMITS.post;
  const body = text.trim();

  const submit = async () => {
    setError(null);
    setPosted(null);
    try {
      setPosted(await post.mutateAsync({ id: chat.id, text: body, pin, file }));
      onDraft(NO_DRAFT);
    } catch (err) {
      setError(errorText(err, t("own_chats.action_error")));
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-xs leading-relaxed text-fg-muted">
        {t(`own_chats.post.hint_${chat.kind}`, {
          account: formatAccountNo(chat.account_seq_no),
        })}
      </p>
      <textarea
        value={text}
        rows={8}
        onChange={(e) => setText(e.target.value)}
        aria-label={t("own_chats.post.text")}
        placeholder={t("own_chats.post.text_placeholder") ?? ""}
        className={cn(TEXTAREA, "min-h-[10rem] resize-y")}
      />
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-fg-muted">
        <span className={body.length > limit ? "text-danger" : undefined}>
          {body.length}/{limit}
        </span>
        <input
          ref={input}
          type="file"
          className="hidden"
          onChange={(e) => {
            setFile(e.target.files?.[0] ?? null);
            e.target.value = "";
          }}
        />
        {file ? (
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="truncate text-fg-secondary">{file.name}</span>
            <button
              type="button"
              className="text-accent hover:underline"
              onClick={() => setFile(null)}
            >
              {t("own_chats.post.file_remove")}
            </button>
          </span>
        ) : (
          <button
            type="button"
            className="text-accent hover:underline"
            onClick={() => input.current?.click()}
          >
            {t("own_chats.post.file_add")}
          </button>
        )}
      </div>
      <label className="flex cursor-pointer items-center gap-2 text-sm text-fg-primary">
        <Checkbox checked={pin} onCheckedChange={(v) => setPin(v === true)} />
        {t("own_chats.post.pin")}
      </label>

      {error && <Alert tone="danger">{error}</Alert>}
      {posted && (
        <Alert
          tone={posted.warning ? "warning" : "success"}
          action={
            <a
              href={posted.url}
              target="_blank"
              rel="noreferrer"
              className="text-sm font-medium text-accent hover:underline"
            >
              {t("own_chats.post.open")}
            </a>
          }
        >
          {posted.warning ?? t("own_chats.post.done")}
        </Alert>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          disabled={(!body && !file) || body.length > limit || post.isPending}
          loading={post.isPending}
          onClick={submit}
          iconLeft={<Send size={15} strokeWidth={2} aria-hidden="true" />}
        >
          {t("own_chats.post.submit")}
        </Button>
        {post.isPending && <span className="text-xs text-fg-muted">{t("own_chats.working")}</span>}
      </div>
    </div>
  );
}
